/**
 * 布局组件样式定义
 * 定义布局相关组件的样式
 */

import { SxProps, Theme } from "@mui/material/styles";
import { layoutUtils, responsive } from "./utils";

// Drawer 组件样式
export const drawerStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    width: 240,
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: 240,
      boxSizing: "border-box",
    },
  },

  // 主内容区域抽屉样式
  main: {
    width: 240,
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: 240,
      boxSizing: "border-box",
    },
  },
};

// DrawerHeader 组件样式
export const drawerHeaderStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    display: "flex",
    alignItems: "center",
    padding: (theme) => theme.spacing(0, 1),
    justifyContent: "flex-end",
    minHeight: "64px !important",
  },

  // 主内容区域抽屉头部样式
  main: {
    display: "flex",
    alignItems: "center",
    padding: (theme) => theme.spacing(0, 1),
    justifyContent: "flex-end",
    minHeight: "64px !important",
    backgroundColor: "transparent",
    color: "primary.dark",
  },

  // 主内容区域抽屉头部标题样式
  mainTitle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    color: "#8E8E8E",
    padding: "5px",
    borderRadius: "0px",
    backgroundColor: "transparent",
  },
};

// List 组件样式
export const listStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    width: "100%",
    bgcolor: "background.paper",
  },
};

// ListItem 组件样式
export const listItemStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },

  // 菜单项样式
  menuItem: {
    borderRadius: "0px",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },

  // 主内容区域列表项样式
  main: {
    display: "block",
    borderRadius: "0px",
    margin: "0px",
    "&.Mui-selected": {
      backgroundColor: "primary.light",
      color: "primary.main",
      borderRadius: "0px",
    },
    "&:hover": {
      backgroundColor: "primary.light",
      color: "primary.main",
      borderRadius: "0px",
    },
  },
};

// ListItemButton 组件样式
export const listItemButtonStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
  },

  // 主内容区域列表项按钮样式
  main: {
    minHeight: 48,
    justifyContent: (open) => (open ? "initial" : "center"),
    px: 2.5,
    color: "inherit",
    borderRadius: "0px",
    margin: "0px",
  },
};

// ListItemIcon 组件样式
export const listItemIconStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
  },

  // 主内容区域列表项图标样式
  main: {
    minWidth: 0,
    mr: (open) => (open ? 3 : "auto"),
    justifyContent: "center",
  },
};

// ListItemText 组件样式
export const listItemTextStyles: Record<string, SxProps<Theme>> = {
  // 主内容区域列表项文本样式
  main: {
    opacity: (open) => (open ? 1 : 0),
  },
};

// Menu 组件样式
export const menuStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    mt: "50px",
  },

  // 用户菜单样式
  userMenu: {
    mt: "50px",
    "& .MuiPaper-root": {
      borderRadius: "0px",
      minWidth: "200px",
      mt: 1,
      "& .MuiMenuItem-root": {
        px: 2,
        py: 1.5,
        gap: 1.5,
      },
    },
  },

  // 通知菜单样式
  notificationMenu: {
    mt: "50px",
    "& .MuiPaper-root": {
      borderRadius: "0px",
      minWidth: "320px",
      maxHeight: "400px",
      mt: 1,
    },
  },

  // 主内容区域用户菜单样式
  mainUserMenu: {
    mt: "45px",
  },
};

// MenuItem 组件样式
export const menuItemStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },

  // 退出菜单项样式
  logout: {
    color: (theme) => theme.palette.error.main,
    "&:hover": {
      backgroundColor: (theme) => theme.palette.error.light,
      color: "white",
    },
  },

  // 颜色继承样式
  inheritColor: {
    color: "inherit",
  },
};

// Badge 组件样式
export const badgeStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    "& .MuiBadge-badge": {
      fontSize: "0.7rem",
      height: "18px",
      minWidth: "18px",
      borderRadius: "0px",
    },
  },
};

// Dialog 组件样式
export const dialogStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    "& .MuiDialog-paper": {
      width: "80%",
      maxHeight: 435,
    },
  },
};

// ToggleButton 组件样式
export const toggleButtonStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
  },

  // 主内容区域切换按钮样式
  main: {
    height: "1.5em",
  },
};

// TabPanel 组件样式
export const tabPanelStyles: Record<string, SxProps<Theme>> = {
  // 容器样式
  container: {
    p: 3,
  },
};

// 导出所有布局组件样式
export default {
  drawerStyles,
  drawerHeaderStyles,
  listStyles,
  listItemStyles,
  listItemButtonStyles,
  listItemIconStyles,
  listItemTextStyles,
  menuStyles,
  menuItemStyles,
  badgeStyles,
  dialogStyles,
  toggleButtonStyles,
  tabPanelStyles,
};
